// Library imports
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// User Component imports
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import { Row, Button, Stack, Card, Text, Pagination, TextInput, Select, Modal, Icon } from "_core/components/common/UI";
import { FitList } from "components/common/parts";
import * as Constants from "_core/configs/Constants";
import * as SceneConfig from "_core/configs/SceneConfig";
import { FormatDateTime, canManageAccount } from "_core/utils/common";
import { generatePassword } from "_core/utils/password";
import { useUpdateLock } from "_core/hooks/useUpdateLock";
import { setLoading, setErrorModal, setInitializeState, setMenuState } from "_core/slices/scene/SceneSlice";
import AdminAccountListApi from "apis/AdminAccountListApi";
import AdminAccountAddApi from "apis/AdminAccountAddApi";
import AdminAccountEditApi from "apis/AdminAccountEditApi";
import AdminAccountDeleteApi from "apis/AdminAccountDeleteApi";

import './AdminUserManage.scss';

const FORM_MODE_ADD = 'add';
const FORM_MODE_EDIT = 'edit';

// 一覧の列定義
const LIST_COLUM_SIZE = { name: 2, email: 3, auth: 2, last_login_date: 2, operation: 2 };
const LIST_TITLE = {
    name: "名前",
    email: "メールアドレス",
    auth: "権限",
    last_login_date: "最終ログイン",
    operation: "",
};
// ソート対象の列(操作列は要素を持つためソートしない)
const LIST_SORT_COLUMN = ["name", "email", "auth", "last_login_date"];

// 権限列の表示ラベル
// 権限は数値のまま保持して権限の強さ順でソートできるようにし、表示だけラベルに置き換える
const BuildAuthCellMap = (accounts) => {
    const authLabelMap = { ...Constants.UserAuthType };
    accounts.forEach((account) => {
        if (authLabelMap[account.auth] === undefined) {
            authLabelMap[account.auth] = "-";
        }
    });
    return { auth: authLabelMap };
};

// コピー完了の表示を元に戻すまでの時間(ms)
const COPIED_LABEL_DURATION = 3000;

// モーダル背景のクリックでは閉じないようにするためのハンドラ
// 未保存の入力が失われるフォームにのみ指定し、閉じる操作は×ボタンとキャンセルボタンに限定する
const IgnoreBackdropClick = () => {};

// Selectに渡す選択肢({表示ラベル: value})を組み立てる
// 権限の順序はサーバーが返す順（強い順）をそのまま使用する
const BuildAuthOptions = (authList) => {
    const options = {};
    authList.forEach((auth) => {
        const label = Constants.UserAuthType[auth];
        if (label) {
            options[label] = String(auth);
        }
    });
    return options;
};

const AdminUserManage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const ExecAdminAccountListApi = AdminAccountListApi();
    const ExecAdminAccountAddApi = AdminAccountAddApi();
    const ExecAdminAccountEditApi = AdminAccountEditApi();
    const ExecAdminAccountDeleteApi = AdminAccountDeleteApi();

    const { initializeState } = useSelector((store) => store.scene);
    const { tAccount } = useSelector((store) => store.transaction);
    // デモユーザーと読み取り専用権限は、どちらも更新系を使わせないためまとめて扱う
    const { isUpdateLocked } = useUpdateLock();

    // 一覧
    const [page, setPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [isListLoading, setIsListLoading] = useState(true);
    const [accountListData, setAccountListData] = useState({
        columsize: LIST_COLUM_SIZE,
        title: LIST_TITLE,
        value: []
    });
    const [authCellMap, setAuthCellMap] = useState(BuildAuthCellMap([]));

    // 操作可否はサーバーの判定結果をそのまま使用する（画面側で権限判定を二重実装しない）
    const [selectableAuthList, setSelectableAuthList] = useState([]);
    const [canAdd, setCanAdd] = useState(false);
    const [loginBlockedAccountIdDict, setLoginBlockedAccountIdDict] = useState({});

    // 追加・編集モーダル
    // TextInputは一度入力すると内部で入力値を保持し、default指定の変更を受け付けなくなる。
    // モーダルは常にマウントされたままのため、開くたびにformKeyを変えてフォームを作り直す。
    const [formModal, setFormModal] = useState({ show: false, mode: FORM_MODE_ADD, target: null, formKey: 0 });
    const [formName, setFormName] = useState("");
    const [formEmail, setFormEmail] = useState("");
    const [formPassword, setFormPassword] = useState("");
    const [formUnlockLogin, setFormUnlockLogin] = useState(false);
    // パスワードを再生成した際に、入力欄へ生成結果を反映させるためのkey
    const [passwordKey, setPasswordKey] = useState(0);
    // コピー完了の表示状態と、表示を元に戻すタイマー
    const [isCopied, setIsCopied] = useState(false);
    const copiedTimer = useRef(null);
    const [formAuth, setFormAuth] = useState("");
    const [isNameValid, setIsNameValid] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isPasswordValid, setIsPasswordValid] = useState(false);

    // 削除確認モーダル
    const [deleteModal, setDeleteModal] = useState({ show: false, target: null });

    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);

    // 画面を離れた後にコピー完了の解除が走らないよう、タイマーを破棄する
    useEffect(() => {
        return () => {
            if (copiedTimer.current) {
                clearTimeout(copiedTimer.current);
            }
        };
    }, []);

    // 権限の無いユーザーがURLを直接指定した場合は403へ遷移する
    // ※画面側のガードはUX目的。実際の制御はサーバー側で行っている
    useEffect(() => {
        if (initializeState < Constants.INITIALIZE_STATE.allComplete) {
            return;
        }
        if (!canManageAccount(tAccount?.auth)) {
            navigate(SceneConfig.PAGES.ERROR_403, { replace: true });
        }
    }, [initializeState, tAccount]);

    useEffect(() => {
        if (initializeState !== Constants.INITIALIZE_STATE.allComplete) {
            return;
        }
        if (!canManageAccount(tAccount?.auth)) {
            return;
        }
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetching));
        LoadList(1);
    }, [initializeState]);


    // ------------------------------------------------------------------
    // 一覧
    // ------------------------------------------------------------------

    const LoadList = async (targetPage) => {
        setIsListLoading(true);

        const { response } = await ExecAdminAccountListApi(targetPage);

        if (response && response.error_code === 0) {
            setPage(targetPage);
            setMaxPage(response.max_page || 1);
            setSelectableAuthList(response.selectable_auth_list || []);
            setCanAdd(response.can_add || false);
            setLoginBlockedAccountIdDict(response.login_blocked_account_id_dict || {});
            BuildListData(response.t_account_list || [], response.login_blocked_account_id_dict || {});
        } else {
            dispatch(setErrorModal({ show: true, text: response.error_detail }));
            BuildListData([], {});
        }

        setIsListLoading(false);
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));
    };

    const BuildListData = (accounts, loginBlockedAccounts) => {
        const listData = {
            columsize: LIST_COLUM_SIZE,
            title: LIST_TITLE,
            value: []
        };

        accounts.forEach((account) => {
            const rowData = {};
            const rowTitle = {};
            const isLoginBlocked = Object.hasOwn(loginBlockedAccounts, account.id);

            // 「自分」は名前の一部に見えないようタグとして表示する
            // (ソートはrowTitle側の素の名前を基準にするため、ここに要素を入れても並び順は変わらない)
            rowData.name = (account.is_self || isLoginBlocked)
                ? (
                    <Row className="account-name-cell align-center spacing-8px">
                        {(account.is_self && <div className="self-tag">自分</div>)}
                        {(isLoginBlocked && <div className="blocked-tag">ログイン制限中</div>)}
                        <div className="account-name-text">{account.name}</div>
                    </Row>
                )
                : account.name;
            rowData.email = account.email;
            // 表示はcellプロパティでラベルに置き換える(権限の強さ順でソートするため数値のまま持つ)
            rowData.auth = account.auth;
            rowData.last_login_date = account.last_login_date ? FormatDateTime(account.last_login_date) : "-";

            rowTitle.name = account.name;
            rowTitle.email = account.email;

            const enableEdit = account.can_edit && !isUpdateLocked;
            const enableDelete = account.can_delete && !isUpdateLocked;

            rowData.operation = (
                <Row className="spacing-8px">
                    <Button
                        className={"operate-button secondary-border" + (enableEdit ? "" : " disabled")}
                        disabled={!enableEdit}
                        onClick={() => { OpenEditModal(account) }}
                    >
                        <Row className="text-size-normal text-normal-weight operate-label">編集</Row>
                    </Button>
                    <Button
                        className={"operate-button secondary-border danger" + (enableDelete ? "" : " disabled")}
                        disabled={!enableDelete}
                        onClick={() => { OpenDeleteModal(account) }}
                    >
                        <Row className="text-size-normal text-normal-weight operate-label">削除</Row>
                    </Button>
                </Row>
            );

            listData.value.push({
                data: rowData,
                title: rowTitle,
                onclick: () => {},
            });
        });

        setAccountListData(listData);
        setAuthCellMap(BuildAuthCellMap(accounts));
    };

    const HandlePageChange = (nextPage) => {
        if (nextPage === page) {
            return;
        }
        LoadList(nextPage);
    };


    // ------------------------------------------------------------------
    // 追加・編集モーダル
    // ------------------------------------------------------------------

    const OpenAddModal = () => {
        setFormName("");
        setFormEmail("");
        // 初期パスワードは自動生成する（生成値は必ず要件を満たすためバリデーション通過済みとして扱う）
        setFormPassword(generatePassword());
        // 付与できる権限のうち、最も弱いものを初期選択にする（現在は「一般（読み取りのみ）」）
        setFormAuth(selectableAuthList.length > 0 ? String(selectableAuthList[selectableAuthList.length - 1]) : "");
        setIsNameValid(false);
        setIsEmailValid(false);
        setIsPasswordValid(true);
        ResetCopied();
        setFormModal({ show: true, mode: FORM_MODE_ADD, target: null, formKey: formModal.formKey + 1 });
    };

    // パスワードを引き直す
    const RegeneratePassword = () => {
        setFormPassword(generatePassword());
        setIsPasswordValid(true);
        // TextInputは一度入力すると内部で値を保持しdefaultの変更を受け付けないため、入力欄を作り直す
        setPasswordKey(passwordKey + 1);
    };

    // コピー完了の表示状態をリセットする
    const ResetCopied = () => {
        if (copiedTimer.current) {
            clearTimeout(copiedTimer.current);
            copiedTimer.current = null;
        }
        setIsCopied(false);
    };

    // 追加したユーザーへ共有できるよう、入力中の内容をまとめてクリップボードへコピーする
    const CopyAccountInfo = () => {
        const text = [
            "名前: " + formName,
            "メールアドレス: " + formEmail,
            "パスワード: " + formPassword,
        ].join("\n");

        // クリップボードAPIはhttps(またはlocalhost)でしか利用できないため、使えない環境ではその旨を伝える
        if (! navigator.clipboard) {
            window.alert("クリップボードへのコピーに失敗しました");
            return;
        }

        navigator.clipboard.writeText(text)
            .then(() => {
                // 押したことが分かるよう、一定時間だけボタンの表示を完了状態にする
                if (copiedTimer.current) {
                    clearTimeout(copiedTimer.current);
                }
                setIsCopied(true);
                copiedTimer.current = setTimeout(() => {
                    copiedTimer.current = null;
                    setIsCopied(false);
                }, COPIED_LABEL_DURATION);
            })
            .catch(() => {
                window.alert("クリップボードへのコピーに失敗しました");
            });
    };

    const OpenEditModal = (account) => {
        setFormName(account.name);
        setFormEmail(account.email);
        setFormPassword("");
        setFormAuth(String(account.auth));
        setFormUnlockLogin(false);
        // 既存の値が入っているため、バリデーションは通過済みとして扱う
        setIsNameValid(true);
        setIsEmailValid(true);
        setIsPasswordValid(true);
        setFormModal({ show: true, mode: FORM_MODE_EDIT, target: account, formKey: formModal.formKey + 1 });
    };

    const CloseFormModal = () => {
        // 入力内容をクリアしておき、閉じるアニメーション中に前回の値が見えないようにする
        setFormName("");
        setFormEmail("");
        setFormPassword("");
        setFormAuth("");
        setFormUnlockLogin(false);
        setIsNameValid(false);
        setIsEmailValid(false);
        setIsPasswordValid(false);
        ResetCopied();
        setFormModal({ show: false, mode: FORM_MODE_ADD, target: null, formKey: formModal.formKey });
    };

    // 編集モーダルの権限選択肢
    // サーバーが返す selectable_auth_list（自分と同格以下）をそのまま使う。
    // 一覧には自分より上位のアカウントが出ないため通常は下の分岐に入らないが、
    // 選択肢に無い権限が来ても選択欄が空にならないよう保険として現在値を先頭に足す。
    const GetFormAuthOptions = () => {
        if (formModal.mode === FORM_MODE_ADD) {
            return BuildAuthOptions(selectableAuthList);
        }

        const currentAuth = formModal.target ? formModal.target.auth : null;
        if (currentAuth === null || selectableAuthList.includes(currentAuth)) {
            return BuildAuthOptions(selectableAuthList);
        }
        return BuildAuthOptions([currentAuth, ...selectableAuthList]);
    };

    // 編集時に、変更があった項目だけをリクエストデータに含める
    const BuildEditRequestData = () => {
        const target = formModal.target;
        if (!target) {
            return {};
        }

        const requestData = {};

        if (formName !== target.name) {
            requestData.name = formName;
        }
        if (formEmail !== target.email) {
            requestData.email = formEmail;
        }
        if (formPassword) {
            requestData.password = formPassword;
        }
        if (Number(formAuth) !== target.auth) {
            requestData.auth = Number(formAuth);
        }
        if (formUnlockLogin) {
            requestData.unlock_login = true;
        }

        return requestData;
    };

    const GetCanSubmit = () => {
        if (isUpdateLocked) {
            return false;
        }
        if (!formName || !formEmail) {
            return false;
        }
        if (!isNameValid || !isEmailValid) {
            return false;
        }

        if (formModal.mode === FORM_MODE_ADD) {
            if (!formAuth) {
                return false;
            }
            // 追加時はパスワードが必須
            return formPassword !== "" && isPasswordValid;
        }

        // 編集時はパスワード未入力なら変更しない
        if (formPassword && !isPasswordValid) {
            return false;
        }
        // 変更内容が無い場合は送信できない
        return Object.keys(BuildEditRequestData()).length > 0;
    };

    const SubmitForm = () => {
        if (!GetCanSubmit()) {
            return;
        }

        if (formModal.mode === FORM_MODE_ADD) {
            ExecAccountAdd();
        } else {
            ExecAccountEdit();
        }
    };


    // ------------------------------------------------------------------
    // 削除確認モーダル
    // ------------------------------------------------------------------

    const OpenDeleteModal = (account) => {
        setDeleteModal({ show: true, target: account });
    };

    const CloseDeleteModal = () => {
        setDeleteModal({ show: false, target: null });
    };


    // ------------------------------------------------------------------
    // API実行
    // ------------------------------------------------------------------

    const ExecAccountAdd = async () => {
        dispatch(setLoading(true));
        const result = await ExecAdminAccountAddApi(formName, formEmail, formPassword, Number(formAuth));
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }

        CloseFormModal();
        // 操作可否や件数が変わるため、必ず一覧を取得し直す
        await LoadList(page);
    };

    const ExecAccountEdit = async () => {
        const target = formModal.target;
        if (!target) {
            return;
        }

        dispatch(setLoading(true));
        const result = await ExecAdminAccountEditApi(target.id, BuildEditRequestData());
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }

        CloseFormModal();
        await LoadList(page);
    };

    const ExecAccountDelete = async () => {
        const target = deleteModal.target;
        if (!target) {
            return;
        }

        dispatch(setLoading(true));
        const result = await ExecAdminAccountDeleteApi(target.id);
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }

        CloseDeleteModal();

        // 表示中のページが最後の1件だった場合は、前のページに戻る
        const nextPage = (accountListData.value.length <= 1 && page > 1) ? page - 1 : page;
        await LoadList(nextPage);
    };


    // ------------------------------------------------------------------
    // 描画
    // ------------------------------------------------------------------

    const formatBlockedUntil = (blockedUntil) => {
        const date = new Date(blockedUntil);

        // 秒・ミリ秒があれば、次の分に切り上げる
        if (date.getSeconds() > 0 || date.getMilliseconds() > 0) {
            date.setMinutes(date.getMinutes() + 1);
        }
        date.setSeconds(0, 0);

        return FormatDateTime(date);
    }

    const renderFormModal = () => {
        const isAdd = formModal.mode === FORM_MODE_ADD;
        // 権限を変更できないアカウントでは、選択欄を操作不可にする
        const authDisabled = !isAdd && !(formModal.target && formModal.target.can_change_auth);
        // 未入力の項目がある状態でコピーしても共有できないため、3項目が揃うまで押せないようにする
        const canCopyAccountInfo = formName !== "" && formEmail !== "" && formPassword !== "";
        // ログイン制限がかかっていない場合は制限解除トグルを押せないようにする
        const accountId = formModal.target ? formModal.target.id : 0;
        let blockedUntil = null;
        if (Object.hasOwn(loginBlockedAccountIdDict, accountId)) {
            blockedUntil = formatBlockedUntil(loginBlockedAccountIdDict[accountId]);
        }

        const modalBody = (
            <Stack key={formModal.formKey} className="account-form spacing-16px padding-16px">
                <Stack className="account-form-row spacing-4px">
                    <Text className="account-form-label text-bold">名前</Text>
                    <TextInput
                        className="width-full"
                        default={formName}
                        onchange={(e) => setFormName(e.target.value)}
                        placeholder="名前を入力してください"
                        autocomplete="off"
                        autocorrect={false}
                        rules={[
                            { type: "required" },
                            { type: "maxLength", value: Constants.USER_NAME_MAX_LENGTH },
                        ]}
                        onValidate={(valid) => { setIsNameValid(valid) }}
                    />
                </Stack>

                <Stack className="account-form-row spacing-4px">
                    <Text className="account-form-label text-bold">メールアドレス</Text>
                    <TextInput
                        className="width-full"
                        type="email"
                        default={formEmail}
                        onchange={(e) => setFormEmail(e.target.value)}
                        placeholder="メールアドレスを入力してください"
                        autocomplete="off"
                        autocorrect={false}
                        rules={[
                            { type: "required" },
                            { type: "maxLength", value: Constants.MAIL_ADDRESS_MAX_LENGTH },
                            { type: "custom", validate: ((value) => value.includes('@')), message: "メールアドレスを入力してください" },
                        ]}
                        onValidate={(valid) => { setIsEmailValid(valid) }}
                    />
                </Stack>

                <Stack className="account-form-row spacing-4px">
                    <Text className="account-form-label text-bold">パスワード</Text>
                    <div className="password-area relative">
                        <TextInput
                            key={'account-password:' + passwordKey}
                            className={"width-full" + (isAdd ? " has-regenerate-button" : "")}
                            // 追加時は共有前に確認できるよう平文で表示し、編集時は伏せ字のままにする
                            type={isAdd ? "text" : "password"}
                            noReveal={true}
                            default={formPassword}
                            onchange={(e) => setFormPassword(e.target.value)}
                            placeholder={isAdd ? "初期パスワードを入力してください" : "変更する場合のみ入力してください"}
                            autocomplete="new-password"
                            autocorrect={false}
                            rules={isAdd ? [
                                { type: "required" },
                                { type: "minLength", value: Constants.USER_PASSWORD_MIN_LENGTH },
                                { type: "maxLength", value: Constants.USER_PASSWORD_MAX_LENGTH },
                            ] : [
                                { type: "minLength", value: Constants.USER_PASSWORD_MIN_LENGTH },
                                { type: "maxLength", value: Constants.USER_PASSWORD_MAX_LENGTH },
                            ]}
                            onValidate={(valid) => { setIsPasswordValid(valid) }}
                        />
                        {isAdd &&
                            <Button
                                className="password-regenerate-button"
                                onClick={RegeneratePassword}
                                title="パスワードを再生成"
                            >
                                <Icon icon="arrow-clockwise" size={18} />
                            </Button>
                        }
                    </div>
                    {isAdd
                        ? <Text className="text-size-small text-color-sub">英大文字・小文字・数字を含む{Constants.USER_PASSWORD_MIN_LENGTH}文字以上{Constants.USER_PASSWORD_MAX_LENGTH}文字以下</Text>
                        : <Text className="text-size-small text-color-sub">未入力の場合、パスワードは変更されません。</Text>
                    }
                </Stack>

                <Stack className="account-form-row spacing-4px">
                    <Text className="account-form-label text-bold">権限</Text>
                    <Select
                        className="width-full"
                        name="account-auth-select"
                        title={GetFormAuthOptions()}
                        value={formAuth}
                        onchange={(value) => setFormAuth(value)}
                        disabled={authDisabled}
                    />
                    {authDisabled &&
                        <Text className="text-size-small text-color-sub">このユーザーの権限は変更できません。</Text>
                    }
                </Stack>

                {!isAdd && blockedUntil &&
                    <Stack className="account-form-unlock-login account-form-row spacing-4px">
                        <Text className="account-form-label text-bold">ログイン制限</Text>
                        <label className={"account-unlock-login-switch " + (formUnlockLogin ? "on" : "off")}>
                            <input type="checkbox" checked={formUnlockLogin}
                                onChange={(e) => setFormUnlockLogin(e.target.checked)} />
                            <span className="switch-track"><span className="switch-thumb" /></span>
                            <span className="switch-label">解除する</span>
                        </label>
                        <Text className="text-size-small">制限時間：{blockedUntil} まで</Text>
                    </Stack>
                }

                {isAdd &&
                    <Row className="copy-account-row width-full">
                        <Button
                            className={"secondary-border copy-account-button" + (canCopyAccountInfo ? "" : " disabled") + (isCopied ? " copied" : "")}
                            disabled={!canCopyAccountInfo}
                            onClick={CopyAccountInfo}
                        >
                            <Row className="flex-center align-center spacing-8px">
                                <Icon icon={isCopied ? "check-lg" : "copy"} size={16} />
                                <div className="text-size-normal text-normal-weight">{isCopied ? "コピーしました" : "ログイン情報をコピー"}</div>
                            </Row>
                        </Button>
                    </Row>
                }
            </Stack>
        );

        return (
            <Modal
                className={formModal.show ? "show" : ""}
                dialogclass="modal-dialog-centered"
                name="account-form-modal"
                title={isAdd ? "ユーザー追加" : "ユーザー編集"}

                onBackdropClick={IgnoreBackdropClick}

                showClose={true}

                showCancel={true}
                cancel={CloseFormModal}
                cancelbtnclass="secondary-border"
                cancelvalue="キャンセル"

                showConfirm={true}
                confirm={SubmitForm}
                confirmbtnclass="primary"
                confirmvalue={isAdd ? "追加" : "保存"}
                confirmdisabled={!GetCanSubmit()}

                footermessageleft={isAdd
                    ? <Text className="account-form-note text-size-small text-color-sub">※追加するとこの画面は閉じます。パスワードは後から確認できません。</Text>
                    : undefined
                }

                body={modalBody}
            />
        );
    };

    const renderDeleteModal = () => {
        const target = deleteModal.target;

        const modalBody = (
            <Stack className="account-delete-message spacing-16px padding-16px">
                <Text>{target ? "「" + target.name + "」を削除します。" : ""}</Text>
                <Text className="text-color-sub">※削除したユーザーは元に戻すことができません。<br />※削除してもよろしいですか？</Text>
            </Stack>
        );

        return (
            <Modal
                className={deleteModal.show ? "show" : ""}
                dialogclass="modal-dialog-centered"
                name="account-delete-modal"
                title="ユーザー削除"

                showClose={false}

                showCancel={true}
                cancel={CloseDeleteModal}
                cancelbtnclass="secondary-border"
                cancelvalue="キャンセル"

                showConfirm={true}
                confirm={ExecAccountDelete}
                confirmbtnclass="danger"
                confirmvalue="削除"

                body={modalBody}
            />
        );
    };

    const enableAdd = canAdd && !isUpdateLocked;

    return (
        <PageContent pageId="AdminUserManage">
            <PageHeader>
                <Row className="flex-both align-center width-full">
                    <Stack className="spacing-4px">
                        <div className="page-header-title">ユーザー管理</div>
                        <div className="page-header-detail">管理画面にログインできるユーザーを追加・編集・削除します。</div>
                    </Stack>
                    <Button
                        className={"primary add-account-button" + (enableAdd ? "" : " disabled")}
                        disabled={!enableAdd}
                        onClick={OpenAddModal}
                    >
                        <Row className="flex-center align-center spacing-8px">
                            <Icon icon="plus" size={20} />
                            <div>ユーザーを追加</div>
                        </Row>
                    </Button>
                </Row>
            </PageHeader>
            <PageWrapper>
                <PageContainer>
                    <Stack className="block-center spacing-32px">
                        <Card className="base-card">
                            <Stack className="width-full spacing-16px">
                                <FitList
                                    list={accountListData}
                                    cell={authCellMap}
                                    header={true}
                                    scrollbar={"scrollbar"}
                                    sortColumnList={LIST_SORT_COLUMN}
                                    isLoading={(isListLoading || (initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete)) ? true : false}
                                />

                                {(!isListLoading && accountListData.value.length === 0) &&
                                    <Text className="text-color-sub">ユーザーが登録されていません。</Text>
                                }

                                <Row className="flex-center align-center width-full">
                                    <Pagination
                                        totalPages={maxPage}
                                        currentPage={page}
                                        onChange={HandlePageChange}
                                        isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                    />
                                </Row>
                            </Stack>
                        </Card>
                    </Stack>
                </PageContainer>

                {renderFormModal()}
                {renderDeleteModal()}
            </PageWrapper>
        </PageContent>
    );
};

export default AdminUserManage;
